// Бар задачи лонча: цвет по показанному статусу, глиф состояния перед названием, дата конца справа внизу,
// ручки растяжения по краям, меню правой кнопкой, рамка чужого внимания.
import { memo, type KeyboardEvent, type PointerEvent } from 'react';
import { dateRange, monthDay } from '@core/dates';
import { Menu, type MenuItem } from '@core/ui/Menu';
import { Tooltip } from '@core/ui/Tooltip';
import { cn } from '@core/ui/cn';
import type { Attention } from '../roadmap/shared/attention';
import type { BarBox } from '../roadmap/shared/geometry';
import { AttentionLabel, ResizeHandles, SELECTION_RING, type DragKind } from '../roadmap/shared/TaskBar';
import { STATUS_LOOK, type LaunchTask, type ShownStatus } from './planner';

type Props = {
  task: LaunchTask; box: BarBox; top: number; height: number; shown: ShownStatus; blockedBy: string[];
  dayWidth: number;          // ширина дня сетки: дата конца рисуется на барах шире трёх дней
  attention?: Attention | undefined; pending?: boolean; ghost?: boolean; selected?: boolean;
  onHover?: (id: string | null) => void;   // связи задачи подсвечиваются, пока курсор на ней
  wrap?: boolean;           // полные названия: перенос на две строки, при нехватке — три мелким; дата уходит вниз
  onPointerDown: (kind: DragKind, e: PointerEvent<HTMLElement>) => void; onOpen: () => void; onKey: (e: KeyboardEvent<HTMLElement>) => void; menu?: MenuItem[];
};

export const LaunchBar = memo(function LaunchBar({ task, box, top, height, shown, blockedBy, dayWidth, attention, pending, ghost, selected, wrap = false, onHover, onPointerDown, onOpen, onKey, menu }: Props) {
  const look = STATUS_LOOK[shown];
  const bar = (
    <div
      role="button" tabIndex={0}
      data-launch-task={task.id} data-status={shown} data-attention={attention ? attention.name : undefined} data-selected={selected ? '' : undefined}
      aria-label={`${task.title}, ${task.start_date} – ${task.end_date}, ${look.label}`}
      className={cn(
        'min-h-0 absolute rounded border font-semibold px-2 flex select-none overflow-hidden cursor-grab focus:outline-none focus-visible:ring-4 focus:ring-4 ring-accent/70',
        wrap ? 'items-start py-1' : 'items-center gap-2', wrap && height >= 60 ? 'text-xs' : 'text-sm',
        box.clippedLeft && 'rounded-l-none', box.clippedRight && 'rounded-r-none', pending && 'opacity-70', ghost && 'opacity-50 z-[3] cursor-grabbing shadow-lg',
      )}
      style={{ left: box.left, width: box.width, top, height, background: look.bg, borderColor: look.border, color: look.text, outline: attention ? `2px solid ${attention.color}` : undefined, outlineOffset: 2, boxShadow: selected ? SELECTION_RING : undefined }}
      onPointerDown={(e) => onPointerDown('move', e)} onDoubleClick={onOpen} onKeyDown={onKey}
      onPointerEnter={() => onHover?.(task.id)} onPointerLeave={() => onHover?.(null)}
    >
      <span className={wrap ? (height >= 60 ? 'line-clamp-3 break-words leading-4' : 'line-clamp-2 break-normal overflow-hidden leading-5') : 'truncate'}>{look.glyph && <span className="mr-1" aria-hidden>{look.glyph}</span>}{task.title}</span>
      {/* дата конца: в одну строку — справа после названия, не налезая; в полных названиях — под текстом справа; у узких баров нет */}
      {box.width >= 3 * dayWidth && (wrap
        ? <span className="absolute right-1.5 bottom-0.5 text-[11px] leading-none opacity-70 tabular-nums" aria-hidden>{monthDay(task.end_date)}</span>
        : <span className="ml-auto shrink-0 text-[11px] opacity-70 tabular-nums" aria-hidden>{monthDay(task.end_date)}</span>)}
      <AttentionLabel attention={attention} />
      <ResizeHandles box={box} onPointerDown={onPointerDown} />
    </div>
  );
  // подсказка та же семья, что у баров роадмапа; тому, кого тащат, не показывается
  const tipped = (
    <Tooltip disabled={ghost} title={task.title} lines={[dateRange(task.start_date, task.end_date), look.label + (blockedBy.length ? `: ${blockedBy.join(', ')}` : ''), task.notes ?? '']} hint="Double-click to open · drag to move">{bar}</Tooltip>
  );
  return menu ? <Menu items={menu}>{tipped}</Menu> : tipped;
});
